import { useCallback, useEffect, useState } from "react";
import { api, apiError, fileUrl } from "@/lib/api";
import { eur } from "@/lib/format";
import UploadField from "@/components/UploadField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Megaphone, Lock, AlertTriangle } from "lucide-react";

function toCents(v) {
  const s = String(v ?? "").replace(",", ".").trim();
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

function AffiliateDialog({ t, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState({
    title: initial.title || "",
    url: initial.url || "",
    description: initial.description || "",
    category: initial.category || "",
    price_eur: initial.price_cents != null ? String(initial.price_cents / 100) : "",
    currency: initial.currency || "EUR",
    image_path: initial.image_path || "",
  });
  const [ackLabel, setAckLabel] = useState(isEdit);
  const [ackCompete, setAckCompete] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (form.title.trim().length < 2) { toast.error(t("aff.err_title")); return; }
    if (!/^https?:\/\//i.test(form.url.trim())) { toast.error(t("aff.err_url")); return; }
    if (!isEdit && (!ackLabel || !ackCompete)) { toast.error(t("aff.err_ack")); return; }
    setSaving(true);
    const cents = toCents(form.price_eur);
    const body = {
      title: form.title.trim(),
      url: form.url.trim(),
      description: form.description.trim(),
      category: form.category.trim(),
      price_cents: cents,
      currency: cents != null ? (form.currency || "EUR") : null,
      image_path: form.image_path || null,
    };
    try {
      if (isEdit) {
        await api.patch(`/coach/affiliate-offerings/${initial.id}`, body);
      } else {
        await api.post("/coach/affiliate-offerings", { ...body, labeling_acknowledged: true });
      }
      toast.success(t("aff.saved"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="aff-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("aff.dialog_edit") : t("aff.dialog_new")}</DialogTitle>
          <DialogDescription>{t("aff.dialog_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aff.f_title")}</label>
            <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder={t("aff.f_title_ph")} data-testid="aff-title" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aff.f_url")}</label>
            <Input value={form.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://…" data-testid="aff-url" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aff.f_desc")}</label>
            <Textarea className="min-h-[70px]" value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder={t("aff.f_desc_ph")} data-testid="aff-desc" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aff.f_category")}</label>
              <Input value={form.category} onChange={(e) => set({ category: e.target.value })} placeholder={t("aff.f_category_ph")} data-testid="aff-category" />
            </div>
            <div className="w-32">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aff.f_price")}</label>
              <Input type="text" inputMode="decimal" value={form.price_eur} onChange={(e) => set({ price_eur: e.target.value })} placeholder={t("aff.f_price_ph")} data-testid="aff-price" />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("aff.f_cover")}</label>
            <UploadField value={form.image_path} onChange={(p) => set({ image_path: p })} kind="photo" accept="image/*" testId="aff-cover" />
            <p className="mt-1 text-xs text-muted-foreground">{t("aff.f_cover_hint")}</p>
          </div>

          <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs text-amber-800">{t("aff.compete_hint")}</p>
          </div>

          {!isEdit && (
            <div className="space-y-2">
              <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border p-3">
                <input type="checkbox" checked={ackLabel} onChange={(e) => setAckLabel(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" data-testid="aff-ack-label" />
                <span className="text-xs text-foreground">{t("aff.ack_label")}</span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-border p-3">
                <input type="checkbox" checked={ackCompete} onChange={(e) => setAckCompete(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" data-testid="aff-ack-compete" />
                <span className="text-xs text-foreground">{t("aff.ack_compete")}</span>
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="aff-cancel">{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="aff-save">{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AffiliateTab({ t }) {
  const [elig, setElig] = useState(null);
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: e } = await api.get("/coach/affiliate-offerings/eligibility");
      setElig(e);
      if (e?.eligible) {
        const { data } = await api.get("/coach/affiliate-offerings");
        setRows(data?.items || []);
      } else {
        setRows([]);
      }
    } catch { setElig({ eligible: false, reason: "affiliate_requires_coach_pro" }); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (o) => {
    if (!window.confirm(t("aff.delete_confirm"))) return;
    try { await api.delete(`/coach/affiliate-offerings/${o.id}`); toast.success(t("aff.deleted")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const toggleActive = async (o) => {
    try { await api.patch(`/coach/affiliate-offerings/${o.id}`, { active: !o.active }); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (elig === null) return <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>;

  if (!elig.eligible) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="aff-locked">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Lock className="h-6 w-6" /></div>
        <p className="mt-4 font-display text-lg text-foreground">{t("aff.locked_title")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t("aff.locked_sub")}</p>
      </div>
    );
  }

  return (
    <div data-testid="aff-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{t("aff.intro")}</p>
        <Button className="gap-1.5 rounded-full" onClick={() => setEditing({})} data-testid="aff-new"><Plus className="h-4 w-4" /> {t("aff.new")}</Button>
      </div>

      {rows === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="aff-empty">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Megaphone className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("aff.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("aff.empty_sub")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((o) => (
            <div key={o.id} data-testid={`aff-item-${o.id}`} className={`flex items-center gap-3 rounded-xl border border-border bg-card p-3 ${o.active === false ? "opacity-60" : ""}`}>
              {o.image_path ? <img src={fileUrl(o.image_path)} alt="" className="hidden h-12 w-20 shrink-0 rounded-lg object-cover sm:block" /> : null}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-semibold text-foreground">{o.title}</p>
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{t("aff.ad_badge")}</span>
                  {o.active === false && <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{t("aff.inactive")}</span>}
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  {o.category ? `${o.category} · ` : ""}{o.price_cents != null ? eur(o.price_cents, o.currency || "EUR") : ""}
                </p>
                <a href={o.url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary hover:underline"><ExternalLink className="h-3 w-3" /> {o.url}</a>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Switch checked={o.active !== false} onCheckedChange={() => toggleActive(o)} data-testid={`aff-toggle-${o.id}`} />
                <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" onClick={() => setEditing(o)} data-testid={`aff-edit-${o.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full text-rose-600" onClick={() => remove(o)} data-testid={`aff-del-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <AffiliateDialog t={t} initial={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
    </div>
  );
}
